/**
 * Trial-expiry dialog — the one moment the funnel had no surface at all
 * (0/77 expired trials ever converted). Offers three ways forward: upgrade to
 * Pro, stay on Personal, or answer one "what would get you to Pro?" question
 * in exchange for 14 more trial days (once per account, server-enforced —
 * see `useTrialExpiryDialog`). The feedback text goes to the backend feedback
 * store only, never to analytics.
 */
import { useState } from 'react';
import { Sparkles, MessageSquarePlus, ArrowLeft, AlertCircle } from 'lucide-react';
import { useForm } from '@tanstack/react-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from '../../../components/ui/toaster';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { UpgradeDialog } from './UpgradeDialog';
import type { TrialExtensionReason } from '../services/entitlementTokenService';
import type { TrialExtensionSubmitResult } from '../hooks/useTrialExpiryDialog';

interface TrialExpiryDialogProps {
  open: boolean;
  /** Whether the once-per-account feedback extension is still available. */
  extensionAvailable: boolean;
  onUpgrade: () => void;
  onContinuePersonal: () => void;
  /** Escape / overlay click — the dialog never re-nags for this expiry. */
  onDismiss: () => void;
  /** Fired once when the user switches to the feedback view. */
  onFeedbackOpened: () => void;
  onSubmitFeedback: (
    reason: TrialExtensionReason,
    details: string | null
  ) => Promise<TrialExtensionSubmitResult>;
}

const REASON_OPTIONS: Array<{ value: TrialExtensionReason; label: string }> = [
  { value: 'price', label: 'The price doesn’t work for me' },
  { value: 'missing_feature', label: 'A feature I need is missing' },
  { value: 'still_evaluating', label: 'I need more time to evaluate' },
  { value: 'company_wont_pay', label: 'My company won’t pay for it' },
  { value: 'other', label: 'Something else' },
];

export function TrialExpiryDialog({
  open,
  extensionAvailable,
  onUpgrade,
  onContinuePersonal,
  onDismiss,
  onFeedbackOpened,
  onSubmitFeedback,
}: TrialExpiryDialogProps) {
  const [view, setView] = useState<'options' | 'feedback'>('options');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: { reason: '' as '' | TrialExtensionReason, details: '' },
    onSubmit: async ({ value }) => {
      if (!value.reason) return;
      setSubmitting(true);
      setSubmitError(null);
      const details = value.details.trim();
      const result = await onSubmitFeedback(value.reason, details ? details : null);
      setSubmitting(false);
      if (result.success) {
        // `extended` false = the account turned out to already be a
        // subscriber — no days were granted, so don't claim any.
        if (result.extended) {
          toast.success({
            title: 'Trial extended',
            description: 'Thanks for the feedback — you have 14 more days of Pro.',
          });
        } else {
          toast.success({
            title: 'Pro already active',
            description: 'Your subscription is active — no trial needed.',
          });
        }
        // The hook closed the dialog; reset for a hypothetical future open.
        setView('options');
        form.reset();
      } else {
        setSubmitError(result.error ?? 'Could not extend the trial.');
      }
    },
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) return;
    // Radix fires this for Escape/overlay AND after our own buttons close the
    // dialog via the hook; the hook's handlers run first and flip `open`, so
    // this path only fires as a real dismiss while still open.
    setView('options');
    form.reset();
    setSubmitError(null);
    onDismiss();
  };

  const openFeedback = () => {
    onFeedbackOpened();
    setSubmitError(null);
    setView('feedback');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[440px]" data-testid="trial-expiry-dialog">
          {view === 'options' ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Your Pro trial has ended
                </DialogTitle>
                <DialogDescription>
                  Thanks for trying Jam SQL Studio Pro. Pick how you’d like to continue — the
                  free Personal mode keeps working either way.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-2 py-2">
                <Button
                  onClick={() => {
                    onUpgrade();
                    setShowUpgrade(true);
                  }}
                  data-testid="trial-expiry-upgrade"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Upgrade to Pro
                </Button>
                {extensionAvailable && (
                  <Button variant="outline" onClick={openFeedback} data-testid="trial-expiry-feedback">
                    <MessageSquarePlus className="mr-2 h-4 w-4" />
                    Tell us what’s missing — get 14 more days
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={onContinuePersonal}
                  data-testid="trial-expiry-continue-personal"
                >
                  Continue with Personal (free, non-commercial)
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageSquarePlus className="h-5 w-5 text-primary" />
                  What would get you to Pro?
                </DialogTitle>
                <DialogDescription>
                  One question, honestly answered — and your trial gets 14 more days on this
                  account.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void form.handleSubmit();
                }}
                className="space-y-4 py-2"
              >
                <form.Field name="reason">
                  {(field) => (
                    <RadioGroup
                      value={field.state.value}
                      onValueChange={(value) =>
                        field.handleChange(value as TrialExtensionReason)
                      }
                      className="gap-2"
                    >
                      {REASON_OPTIONS.map((option) => (
                        <div key={option.value} className="flex items-center gap-2">
                          <RadioGroupItem
                            value={option.value}
                            id={`trial-expiry-reason-${option.value}`}
                            data-testid={`trial-expiry-reason-${option.value}`}
                          />
                          <Label
                            htmlFor={`trial-expiry-reason-${option.value}`}
                            className="font-normal"
                          >
                            {option.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                </form.Field>
                <form.Field name="details">
                  {(field) => (
                    <Textarea
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Anything specific? (optional — e.g. the feature or price point)"
                      rows={3}
                      maxLength={2000}
                      data-testid="trial-expiry-details"
                    />
                  )}
                </form.Field>
                {submitError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{submitError}</AlertDescription>
                  </Alert>
                )}
                <DialogFooter className="gap-2 sm:justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setView('options')}
                    disabled={submitting}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <form.Subscribe selector={(state) => state.values.reason}>
                    {(reason) => (
                      <Button
                        type="submit"
                        disabled={!reason || submitting}
                        data-testid="trial-expiry-submit"
                      >
                        {submitting ? (
                          <RandomDotmSpinner size={16} className="mr-2" />
                        ) : (
                          <MessageSquarePlus className="mr-2 h-4 w-4" />
                        )}
                        Send &amp; extend trial
                      </Button>
                    )}
                  </form.Subscribe>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <UpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} />
    </>
  );
}
